import { isNativePlatform } from "@repo/analytics";

export const SALESIQ_WIDGET_URL: string =
  "https://salesiq.zohopublic.com/widget?wc=siqf513f6e297a40ad559ffe7a9dede30bcd8f34a0fdbe605db26fd1ce484097825";

// 'https://salesiq.zohopublic.com/widget?wc=siqbf08c3f6cea5564034164647746e34f147cab389d26909dac7c31089577e9665'; // staging url

// WhatsApp channel disabled — see getSalesIQReadyScript() below.
// export const SALESIQ_WHATSAPP_URL: string =
//   'https://wa.me/918448027888?text=Hi%20Team%2C%20I%20need%20assistance.%20Could%20you%20please%20assist%3F';

export const SALESIQ_BOOTSTRAP_SCRIPT: string =
  "window.$zoho=window.$zoho || {};$zoho.salesiq=$zoho.salesiq||{ready:function(){}}";

const SCRIPT_ID_BOOTSTRAP: string = "zoho-salesiq-bootstrap";
const SCRIPT_ID_WIDGET: string = "zsiqscript";

// Browser sessionStorage key that flags an assisted-sales session. Each app
// writes it from its own backend session; the widget skips loading when true.
export const ASSISTED_USER_STORAGE_KEY: string = "isAssistedUser";

export type SalesIQVisitorInfo = Record<string, string>;

const MOBILE_UA_REGEX =
  /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

// Internal-only (Platform tagging, button repositioning). Use shouldLoadSalesIQ()/isAppDevice() for gating.
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return MOBILE_UA_REGEX.test(navigator.userAgent);
}

// No UA fallback (matches third-party in-app browsers too). Reuses @repo/analytics' Capacitor check.
export function isAppDevice(): boolean {
  if (typeof window === "undefined") return false;
  return isNativePlatform();
}

export function isAssistedUserFromStorage(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(ASSISTED_USER_STORAGE_KEY) === "true";
}

// Single gate for both callers: SalesIQ loads on desktop and mobile web, never
// inside the native app.
export function shouldLoadSalesIQ(): boolean {
  return !isAppDevice();
}

function getSalesIQReadyScript(): string {
  // WhatsApp channel disabled — uncomment to re-enable the WhatsApp
  // action_item on the SalesIQ widget's home screen.
  // const whatsappUrl = SALESIQ_WHATSAPP_URL.replace(/'/g, "\\'");
  // return [
  //   '$zoho.salesiq.ready=function(){',
  //   "  $zoho.salesiq.set('home.widgets',[",
  //   '    {',
  //   '      type:"action_item",',
  //   '      title:"WhatsApp",',
  //   '      icon:{type:"channel",name:"whatsapp",url:"' + whatsappUrl + '"}',
  //   '    }',
  //   '  ]);',
  //   '};',
  // ].join('');
  return "$zoho.salesiq.ready=function(){};";
}

// Chains `next` onto `zoho.salesiq.ready`, isolating the prior callback in
// try/catch so a throw there can't stop `next` from running.
function chainReady(zoho: any, next: () => void): void {
  const prevReady = zoho.salesiq.ready;
  zoho.salesiq.ready = function () {
    try {
      if (typeof prevReady === "function") prevReady();
    } catch (err) {
      console.error("[salesiq] prior ready callback failed:", err);
    }
    next();
  };
}

// Visitor info is merged into one module-level object and `ready` is wrapped
// exactly once, so repeated calls (wakanda-ui calls this on every response)
// neither stack wrappers nor drop earlier keys such as `Platform`.
let visitorInfoState: SalesIQVisitorInfo = {};
let readyHooked = false;

function applyVisitorInfo(zoho: any): void {
  zoho.salesiq.visitor?.info(visitorInfoState);
  const contactNumber = visitorInfoState["Phone"];
  if (contactNumber) zoho.salesiq.visitor?.contactnumber(contactNumber);
}

export function setSalesIQVisitorInfo(visitorInfo: SalesIQVisitorInfo): void {
  if (typeof window === "undefined") return;
  visitorInfoState = { ...visitorInfoState, ...visitorInfo };

  const zoho = (window as any).$zoho;
  if (!zoho?.salesiq) return;

  if (!readyHooked) {
    readyHooked = true;
    chainReady(zoho, () => applyVisitorInfo(zoho));
  }

  applyVisitorInfo(zoho);
}

// On mobile the SalesIQ floating button takes the slot the WhatsApp floating
// chat used to occupy: bottom-right, 72px up (110px on the homepage, which has
// an extra bottom bar). The homepage flag is a <html> attribute kept in sync
// with SPA navigation so a single CSS rule can pick the right offset.
const MOBILE_POSITION_STYLE_ID = "zoho-salesiq-mobile-position";
const HOME_ATTR = "data-siq-home";

function syncHomepageFlag(): void {
  const isHome = window.location.pathname === "/";
  document.documentElement.toggleAttribute(HOME_ATTR, isHome);
}

function applyMobilePosition(): void {
  if (document.getElementById(MOBILE_POSITION_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = MOBILE_POSITION_STYLE_ID;
  style.textContent = [
    "#zsiq_float,.zsiq_floatmain{bottom:72px !important;right:24px !important;left:auto !important;}",
    `html[${HOME_ATTR}] #zsiq_float,html[${HOME_ATTR}] .zsiq_floatmain{bottom:110px !important;}`,
  ].join("");
  document.head.appendChild(style);

  syncHomepageFlag();
  window.addEventListener("popstate", syncHomepageFlag);
  (["pushState", "replaceState"] as const).forEach((method) => {
    const original = history[method];
    history[method] = function (
      this: History,
      ...args: Parameters<History["pushState"]>
    ) {
      const result = original.apply(this, args);
      syncHomepageFlag();
      return result;
    };
  });
}

// Mobile back button closes the SalesIQ popup instead of navigating away:
// push a history entry on open, pop it on close (via back button or the
// widget's own X). Registered inside `ready` since `floatwindow` only
// exists once the widget has booted.
function bindMobileBackButtonClose(): void {
  const zoho = (window as any).$zoho;
  if (!zoho?.salesiq) return;

  let isOpen = false;

  window.addEventListener("popstate", () => {
    if (!isOpen) return;
    isOpen = false;
    zoho.salesiq.floatwindow?.visible?.("hide");
  });

  chainReady(zoho, () => {
    if (
      typeof zoho.salesiq.floatwindow?.open !== "function" ||
      typeof zoho.salesiq.floatwindow?.close !== "function"
    ) {
      console.error(
        "[salesiq] floatwindow.open/close unavailable — mobile back-to-close is not active.",
      );
      return;
    }

    zoho.salesiq.floatwindow.open(function () {
      if (isOpen) return;
      isOpen = true;
      history.pushState(null, "", window.location.href);
    });

    zoho.salesiq.floatwindow.close(function () {
      // No-op if the back button already closed it (entry already consumed).
      if (!isOpen) return;
      isOpen = false;
      history.back();
    });
  });
}

export function loadSalesIQ(): void {
  if (typeof document === "undefined" || !document.head) return;

  const doc = document;
  const isMobile = isMobileDevice();

  if (!doc.getElementById(SCRIPT_ID_BOOTSTRAP)) {
    const bootstrap = doc.createElement("script");
    bootstrap.id = SCRIPT_ID_BOOTSTRAP;
    bootstrap.textContent =
      SALESIQ_BOOTSTRAP_SCRIPT + "\n" + getSalesIQReadyScript();
    doc.head.appendChild(bootstrap);

    // IMPL-5920: tag visitor info with the platform so mobile-experiment leads
    // and chat sessions can be attributed/separated from the existing desktop
    // traffic in SalesIQ/CRM reporting.
    setSalesIQVisitorInfo({
      Platform: isMobile ? "mobile-web" : "desktop-web",
    });
    if (isMobile) bindMobileBackButtonClose();
  }

  if (isMobile) applyMobilePosition();

  if (!doc.getElementById(SCRIPT_ID_WIDGET)) {
    const widget = doc.createElement("script");
    widget.id = SCRIPT_ID_WIDGET;
    widget.src = SALESIQ_WIDGET_URL;
    widget.defer = true;
    doc.head.appendChild(widget);
  }
}
